/**
 * TypeScript mirror of `tokens.css` (Figma KRqV8ZIq2EQDOXRjCem6BC, page "Mobile — Flows v2").
 * Use it where a value has to reach JS — the PWA manifest colors, canvas fallbacks, tests.
 * `tokens.css` stays the single source for styling; this file must not drift from it.
 */
export const mobileColors = {
  bg: '#0b0b14',
  surface: '#15152a',
  surface2: '#1e1e36',
  surface3: '#2b2b4a',
  /** Pressed `surface3` fill (press system, `styles/index.css`). */
  surface4: '#38385c',
  text: '#f4f3fa',
  muted: '#a09cbc',
  white: '#ffffff',
  pink: '#ff2d87',
  /** The pink that may sit *under* text — see `tokens.css` (UX-403). */
  pinkStrong: '#cc246c',
  /** Pressed filled pink — 6.38:1 under `text`. */
  pinkPress: '#a81d59',
  cyan: '#19e3ff',
  danger: '#ff7359',
  success: '#3ed6a5',
  onAccent: '#0b0b14',
} as const;

export type MobileColor = keyof typeof mobileColors;

export const mobileLayout = {
  /** Reference viewport of every Figma mobile frame. */
  viewport: { width: 390, height: 844 },
  gutter: 24,
  stack: 14,
  fieldHeight: 52,
  tabBarHeight: 83,
} as const;

export const mobileRadii = {
  chip: 10,
  field: 12,
  button: 14,
  card: 16,
  sheet: 20,
} as const;

/**
 * Minimum touch target of the UX brief (QA 2026-09-15, UX-121). {@link TAP_TARGET_CLASS} is the
 * global class `styles/index.css` defines for it: it centres an invisible `::before` over the
 * control so the finger target reaches 44 x 44 without moving anything on screen.
 */
export const TAP_TARGET_PX = 44;
export const TAP_TARGET_CLASS = 'akm-tap';

/**
 * Press (touch feedback) classes — the global, unlayered rules in `styles/index.css`. Every
 * pressable on the phone carries exactly one of them next to {@link TAP_TARGET_CLASS}; they
 * scale only the visual (the `akm-tap` hit area is untouched), animate only transform, opacity
 * and background-color, never use `:hover`, and skip `:disabled` / `aria-busy` controls.
 */
/** A row on `surface` (song, artist, playlist, settings rows, search pill): surface-2 + 0.98. */
export const PRESS_ROW_CLASS = 'akm-press-row';
/** A card with its own fill (hero, playlist cover, avatar): 0.97 + opacity 0.85. */
export const PRESS_CARD_CLASS = 'akm-press-card';
/** A full-width filled pink button: `--akm-pink-press` + 0.98. */
export const PRESS_FILL_CLASS = 'akm-press-fill';
/** A round filled pink button (play/pause, the row's "+"): `--akm-pink-press` + 0.94. */
export const PRESS_ROUND_CLASS = 'akm-press-round';
/** A `surface-3` fill (next, unselected tone chip): `--akm-surface-4`, no scale. */
export const PRESS_RAISED_CLASS = 'akm-press-raised';
/** An outline button: fills with surface-2, border unchanged. */
export const PRESS_OUTLINE_CLASS = 'akm-press-outline';
/** A text action ("Ver todas", "Cancelar", "Editar"): opacity 0.6. */
export const PRESS_TEXT_CLASS = 'akm-press-text';
/** An icon-only action (back, clear): glyph at 0.6 over a surface-2 pill. */
export const PRESS_ICON_CLASS = 'akm-press-icon';
/** A tab (tab bar, search tabs, segmented tabs): 0.92, inactive ink goes to `--akm-text`. */
export const PRESS_TAB_CLASS = 'akm-press-tab';
/**
 * Colour only — no scale, no transition and no `touch-action` of its own — for the swipe /
 * drag queue row, whose `translateX`, dnd-kit transform and `touch-pan-y` must stay its own.
 * Defaults to surface-2; set `--akm-press-bg` to tint with another token.
 */
export const PRESS_TINT_CLASS = 'akm-press-tint';
/** Inline 16 px busy spinner in `currentColor` at 0.7 (aria-hidden; the label stays). */
export const BUSY_SPINNER_CLASS = 'akm-busy-spinner';
